import _asyncToGenerator from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["appearance", "className", "before", "after", "overrideConsent", "customFormIdentifier", "id", "additionalTrackEvent", "buttonText", "buttonUrl", "successButtonText", "successMessageAppearance", "successMessageHeading", "successMessageDescription", "loadingSuccess", "layout", "compact", "compactMessage", "borderlessMessage", "resourceType", "customDisclaimer", "customData", "onSubmit", "buttonColor", "inputProps", "displaySuccessMessage", "enableJobTitleField"];
import _regeneratorRuntime from "/workdir/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from '@emotion/react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppearance, AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext';
import Button from '@segment/matcha/components/Button';
import Loader from '@segment/matcha/components/Loader';
import { richTextDisclaimer } from '@segment/templates/Forms/EmailCaptureForm';
import { CompanyEmailField, companyEmailMessages, CustomFormIdentifierField, JobTitle, jobTitleMessages } from '@segment/templates/FormFields';
import FormSubmitAction from '@segment/templates/FormSubmitAction';
import Disclaimer from '@segment/templates/Disclaimer';
import TrackableForm, { errorMessage } from '@segment/templates/TrackableForm';
import { trackEmailCaptureForm } from '@segment/tracking/scripts/helpers';
import useMatchMedia from '@segment/utils/useMatchMedia';
import useScrollToAnchor from '@segment/utils/useScrollToAnchor';
import sharedEmailRegex from '@segment/utils/regexes/sharedEmail';
import emailRegex from '@segment/utils/regexes/email';
import formRegex from '@segment/utils/regexes/form';
import * as views from './EmailCaptureForm.views';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var schema = z.object({
  email: z.string().min(1, {
    message: companyEmailMessages.required
  }).regex(formRegex, {
    message: companyEmailMessages.invalid
  }).regex(emailRegex, {
    message: companyEmailMessages.invalid
  }).regex(sharedEmailRegex, {
    message: companyEmailMessages.sharedEmail
  }),
  jobTitle: z.string().min(1, {
    message: jobTitleMessages.required
  }).optional()
});
export var EmailCaptureForm = function EmailCaptureForm(_ref) {
  var appearance = _ref.appearance,
      className = _ref.className,
      before = _ref.before,
      after = _ref.after,
      _ref$overrideConsent = _ref.overrideConsent,
      overrideConsent = _ref$overrideConsent === void 0 ? false : _ref$overrideConsent,
      customFormIdentifier = _ref.customFormIdentifier,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? "email-capture-form" : _ref$id,
      additionalTrackEvent = _ref.additionalTrackEvent,
      _ref$buttonText = _ref.buttonText,
      buttonText = _ref$buttonText === void 0 ? "Download the report" : _ref$buttonText,
      buttonUrl = _ref.buttonUrl,
      successButtonText = _ref.successButtonText,
      successMessageAppearance = _ref.successMessageAppearance,
      successMessageHeading = _ref.successMessageHeading,
      successMessageDescription = _ref.successMessageDescription,
      _ref$loadingSuccess = _ref.loadingSuccess,
      loadingSuccess = _ref$loadingSuccess === void 0 ? false : _ref$loadingSuccess,
      _ref$layout = _ref.layout,
      layout = _ref$layout === void 0 ? "row" : _ref$layout,
      _ref$compact = _ref.compact,
      compact = _ref$compact === void 0 ? false : _ref$compact,
      _ref$compactMessage = _ref.compactMessage,
      compactMessage = _ref$compactMessage === void 0 ? false : _ref$compactMessage,
      _ref$borderlessMessag = _ref.borderlessMessage,
      borderlessMessage = _ref$borderlessMessag === void 0 ? false : _ref$borderlessMessag,
      _ref$resourceType = _ref.resourceType,
      resourceType = _ref$resourceType === void 0 ? "guide" : _ref$resourceType,
      customDisclaimer = _ref.customDisclaimer,
      customData = _ref.customData,
      onSubmitParent = _ref.onSubmit,
      _ref$buttonColor = _ref.buttonColor,
      buttonColor = _ref$buttonColor === void 0 ? "primary" : _ref$buttonColor,
      inputProps = _ref.inputProps,
      _ref$displaySuccessMe = _ref.displaySuccessMessage,
      showSuccessMessage = _ref$displaySuccessMe === void 0 ? true : _ref$displaySuccessMe,
      _ref$enableJobTitleFi = _ref.enableJobTitleField,
      enableJobTitleField = _ref$enableJobTitleFi === void 0 ? false : _ref$enableJobTitleFi,
      rest = _objectWithoutProperties(_ref, _excluded);

  var scrollTo = useScrollToAnchor("".concat(id, "-wrapper"), -96);
  var theme = useTheme();
  var formAppearance = useAppearance(appearance); // Initialize form methods

  var methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: zodResolver(schema)
  });
  var mobileResolutionMediaQuery = theme.breakpoints.down("md", false);
  var isSmallResolution = useMatchMedia(mobileResolutionMediaQuery, true);
  var successMessage = {
    heading: successMessageHeading || "You\u2019re all set!",
    description: successMessageDescription || "Check your inbox for an email confirmation with a link to the ".concat(resourceType, ". You can also click the link below to access now."),
    buttonUrl: buttonUrl,
    buttonText: successButtonText
  };

  var passJobTitleToTracking = function passJobTitleToTracking(data, customData) {
    if (enableJobTitleField) {
      return _objectSpread(_objectSpread({}, customData), {}, {
        jobTitle: data.jobTitle
      });
    }

    return customData;
  };

  return _jsx(AppearanceProvider, {
    appearance: formAppearance,
    children: _jsx(views.FormWrapper, {
      id: "".concat(id, "-wrapper"),
      children: _jsxs(TrackableForm, _objectSpread(_objectSpread({
        layout: layout,
        gap: "small",
        methods: methods,
        className: className,
        before: before,
        after: _jsxs(_Fragment, {
          children: [_jsx(Disclaimer, {
            data: customDisclaimer || richTextDisclaimer(resourceType),
            appearance: successMessageAppearance
          }), after]
        }),
        success: showSuccessMessage && _jsx(_Fragment, {
          children: loadingSuccess ? _jsx(views.LoaderWrapper, {
            children: _jsx(Loader, {})
          }) : _jsx(FormSubmitAction, _objectSpread({
            form: "emailCapture",
            status: "success",
            "data-hook": "email-capture-form-success-message",
            buttonColor: buttonColor,
            appearance: successMessageAppearance,
            compact: compactMessage,
            borderless: borderlessMessage
          }, successMessage))
        }),
        error: _jsx(FormSubmitAction, _objectSpread({
          form: "emailCapture",
          status: "error",
          "data-hook": "email-capture-form-error-message"
        }, errorMessage)),
        onSubmit: /*#__PURE__*/function () {
          var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
            return _regeneratorRuntime.wrap(function _callee$(_context) {
              while (1) {
                switch (_context.prev = _context.next) {
                  case 0:
                    _context.next = 2;
                    return trackEmailCaptureForm(data, additionalTrackEvent, passJobTitleToTracking(data, customData));

                  case 2:
                    if (isSmallResolution) scrollTo();
                    if (onSubmitParent) onSubmitParent(data);

                  case 4:
                  case "end":
                    return _context.stop();
                }
              }
            }, _callee);
          }));

          return function (_x) {
            return _ref2.apply(this, arguments);
          };
        }(),
        overrideConsent: overrideConsent,
        customFormIdentifier: customFormIdentifier
      }, rest), {}, {
        children: [!compact && _jsxs(_Fragment, {
          children: [_jsx(CompanyEmailField, _objectSpread({
            id: "".concat(id, "-email-field")
          }, inputProps)), enableJobTitleField && _jsx(JobTitle, {
            label: "",
            placeholder: "Your job title"
          }), customFormIdentifier && _jsx(CustomFormIdentifierField, {
            value: customFormIdentifier,
            id: id
          }), _jsx(views.ButtonWrapper, {
            children: _jsx(Button, {
              color: buttonColor,
              variant: "filled",
              size: "large",
              expanded: true,
              children: buttonText
            })
          })]
        }), compact && _jsxs(views.Compact, {
          children: [_jsx(CompanyEmailField, _objectSpread({
            id: "".concat(id, "-email-field")
          }, inputProps)), enableJobTitleField && _jsx(JobTitle, {
            label: "",
            placeholder: "Your job title"
          }), customFormIdentifier && _jsx(CustomFormIdentifierField, {
            value: customFormIdentifier,
            id: id
          }), _jsx(views.ButtonWrapper, {
            children: _jsx(Button, {
              color: buttonColor,
              variant: "filled",
              size: "large",
              expanded: true,
              children: buttonText
            })
          })]
        })]
      }))
    })
  });
};
export default EmailCaptureForm;